import { useEffect, useRef, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { CalendarIcon, FileText, Globe, Loader2, MessageSquareText, MoreHorizontal, Pencil, Plus, RefreshCw, Search, Sparkles, Tag, Trash2, Upload, X } from "lucide-react";
import { isDemoMode } from "@/lib/demo/demoMode";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { InfoTip, useStoredSettings } from "@/components/admin_dashboard/WorkflowSettingsFields";
import ContactAssociation from "./ContactAssociation";
import {
  createKnowledgeItem,
  fieldsAsMarkdown,
  filterKnowledgeItems,
  knowledgeItemAsMarkdown,
  markdownToFields,
  validateKnowledgeItem,
  type KnowledgeItem,
} from "./model";

// General knowledge: every note, document and website Dibs can use, in one
// table. Written entries are edited as key and value fields. Documents and
// websites keep their original source and show the text Dibs read from it.
// An entry's category is saved with it, so the whole team sees it. A category
// added but not used yet stays in this browser until an entry uses it.

const MAX_CATEGORY = 40;
const MAX_KEY = 80;
const NO_CATEGORY = "__none";
const ADD_CATEGORY = "__add";
const DEFAULT_CATEGORIES = ["Policy", "Offer", "FAQ"];

interface CategoryStore {
  categories: string[];
  byItem: Record<string, string>;
}

interface FieldRow {
  id: string;
  key: string;
  value: string;
  /** FAQs only: whether Dibs may use this question. */
  active?: boolean;
}

const uid = () => crypto.randomUUID();

const sourceLabel = (item: KnowledgeItem) =>
  item.type === "file" || item.type === "website" ? "Uploaded" : "Written";

const isActive = (item: KnowledgeItem) => item.enabled !== false && item.status !== "Paused";

/** Offers saved as Markdown headings keep their details and dates in the text. */
function normalizeOffer(item: KnowledgeItem): KnowledgeItem {
  if (item.type !== "offer" || !/^##\s+/m.test(item.content)) return item;
  const fields = markdownToFields(item.content);
  const read = (title: string) => fields.find((field) => field.title.trim().toLocaleLowerCase() === title)?.content.trim() ?? "";
  return {
    ...item,
    content: read("offer details") || item.content,
    validFrom: item.validFrom || read("starts on"),
    validUntil: item.validUntil || read("ends on"),
  };
}

/** Written entries as fields; an offer's dates show as Starts on and Ends on fields. */
function offerRows(item: KnowledgeItem): FieldRow[] {
  const rows = markdownToFields(item.content).map((field) => ({ id: uid(), key: field.title, value: field.content }));
  if (item.type !== "offer") return rows;
  for (const [key, value] of [["Starts on", item.validFrom], ["Ends on", item.validUntil]] as const) {
    const existing = rows.find((row) => row.key.trim().toLocaleLowerCase() === key.toLocaleLowerCase());
    if (existing) existing.value ||= value ?? "";
    else rows.push({ id: uid(), key, value: value ?? "" });
  }
  return rows;
}

function faqRows(item: KnowledgeItem): FieldRow[] {
  if (item.content.trim()) {
    return markdownToFields(item.content).map((field) => ({ id: uid(), key: field.title, value: field.content, active: true }));
  }
  const questions = item.questions ?? [];
  return questions.length
    ? questions.map((question) => ({ id: question.id || uid(), key: question.question, value: question.answer, active: question.active }))
    : [{ id: uid(), key: "", value: "", active: true }];
}

function DateField({ value, label, onChange }: { value: string; label: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const selected = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={label}
          className="h-10 w-full justify-between bg-background px-3 font-normal"
        >
          <span className={selected ? "text-foreground" : "text-muted-foreground"}>
            {selected ? format(selected, "dd/MM/yyyy") : "Select date"}
          </span>
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => {
            onChange(date ? format(date, "yyyy-MM-dd") : "");
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function StatusDot({ status }: { status: KnowledgeItem["status"] }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-muted-foreground">
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          status === "Ready" ? "bg-emerald-500"
            : status === "Needs attention" ? "bg-amber-500"
              : status === "Processing" ? "animate-pulse bg-blue-500"
                : "bg-muted-foreground/50"
        }`}
        aria-hidden="true"
      />
      {status}
    </span>
  );
}

/** A written entry is edited as fields; documents and websites are read-only sources. */
const isEditableText = (item: KnowledgeItem) =>
  !item.associatedContact && (item.type === "note" || (item.type === "file" && !!item.isMarkdownFile));

const dateKey = (key: string) => {
  const name = key.trim().toLocaleLowerCase();
  return name === "starts on" ? "from" : name === "ends on" ? "until" : null;
};
const rowDate = (rows: FieldRow[], which: "from" | "until") =>
  rows.find((row) => dateKey(row.key) === which)?.value.trim() || undefined;

const composeContent = (rows: FieldRow[]) => {
  // A date field left empty is simply not set.
  const used = rows.filter((row) => (row.key.trim() || row.value.trim()) && !(dateKey(row.key) && !row.value.trim()));
  if (used.length === 0) return "";
  if (used.length === 1 && used[0].key.trim().toLocaleLowerCase() === "content") return used[0].value.trim();
  return fieldsAsMarkdown(used.map((row) => ({ title: row.key, content: row.value })));
};

interface PanelProps {
  item: KnowledgeItem;
  isNew: boolean;
  categories: string[];
  initialCategory: string;
  saving: boolean;
  busy: boolean;
  onAddCategory: (name: string) => string;
  /** `active` is passed for every written entry, which can be switched on and off. */
  onSave: (item: KnowledgeItem, category: string, active?: boolean) => Promise<boolean>;
  onDelete: (item: KnowledgeItem) => void;
  onOpenFile: (item: KnowledgeItem) => void;
  onRefresh: (item: KnowledgeItem) => Promise<void>;
  onClose: () => void;
}

const emptyRow = (active?: boolean): FieldRow => ({ id: uid(), key: "", value: "", ...(active === undefined ? {} : { active }) });

function KnowledgePanel({
  item: original, isNew, categories, initialCategory, saving, busy,
  onAddCategory, onSave, onDelete, onOpenFile, onRefresh, onClose,
}: PanelProps) {
  const item = normalizeOffer(original);
  // Customer notes are managed in the profile; the public demo is read-only.
  const readOnly = !!item.associatedContact || isDemoMode();
  const isFaq = item.type === "faq";
  const isOffer = item.type === "offer";
  const editableText = isEditableText(item);
  const [name, setName] = useState(item.name);
  const [url, setUrl] = useState(item.url ?? "");
  const [category, setCategory] = useState(initialCategory);
  const [rows, setRows] = useState<FieldRow[]>(() =>
    isFaq ? faqRows(item)
      : isNew ? (isOffer ? [emptyRow(), { ...emptyRow(), key: "Starts on" }, { ...emptyRow(), key: "Ends on" }] : [emptyRow()])
        : offerRows(item));
  const [active, setActive] = useState(isNew ? true : isActive(item));
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const usedRows = rows.filter((row) => row.key.trim() || row.value.trim());
  const draft: KnowledgeItem = {
    ...item,
    name,
    url: item.type === "website" ? url.trim() : item.url,
    ...(isFaq
      ? {
        content: "",
        questions: usedRows.map((row) => ({ id: row.id, question: row.key, answer: row.value, active: row.active !== false })),
      }
      : isOffer
        ? { content: composeContent(rows), validFrom: rowDate(rows, "from"), validUntil: rowDate(rows, "until") }
        : { content: editableText ? composeContent(rows) : item.content }),
  };

  const preview = isFaq || isOffer
    ? [`# ${name.trim() || "Untitled"}`, knowledgeItemAsMarkdown(draft) || "(Nothing written yet.)"].join("\n\n")
    : editableText
      ? [`# ${name.trim() || "Untitled"}`, draft.content || "(Nothing written yet.)"].join("\n\n")
      : item.content
        || (item.status === "Processing" ? "Dibs is still reading this. Check back in a moment."
          : item.status === "Needs attention" ? "Dibs couldn't read this. Try refreshing it."
            : isNew ? "Save to let Dibs read it." : "No text is available yet.");

  const addCategory = () => {
    const resolved = onAddCategory(newCategory);
    if (!resolved) return;
    setCategory(resolved);
    setNewCategory("");
    setAddingCategory(false);
  };

  const submit = async () => {
    const message = validateKnowledgeItem(draft);
    setError(message);
    if (message) return;
    const saved = await onSave(draft, category, readOnly ? undefined : active);
    if (saved) onClose();
  };

  const patchRow = (id: string, patch: Partial<FieldRow>) =>
    setRows(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const fieldsEditor = (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5">
        <h3 className="text-sm font-semibold text-foreground">Fields</h3>
        <InfoTip label="About fields">
          {isFaq
            ? "Each field is a name and its value, like a question and its answer. Turn a field off to stop Dibs using it without deleting it."
            : "Each field is a name and its value, like Price and $25. Dibs reads each one as a heading followed by its text."}
        </InfoTip>
      </div>
      {rows.map((row, index) => (
        <div key={row.id} className={index > 0 ? "space-y-2 border-t border-border pt-4" : "space-y-2"}>
          <div className="flex items-center gap-2">
            <Input
              aria-label={`Field ${index + 1} name`}
              value={row.key}
              maxLength={MAX_KEY * (isFaq ? 3 : 1)}
              placeholder={isFaq ? "Field name, e.g. Do I need experience?" : "Field name, e.g. Price"}
              className="font-medium"
              onChange={(event) => patchRow(row.id, { key: event.target.value })}
            />
            {isFaq && (
              <Switch
                checked={row.active !== false}
                onCheckedChange={(next) => patchRow(row.id, { active: next })}
                aria-label={`${row.active !== false ? "Turn off" : "Turn on"} ${row.key || `field ${index + 1}`}`}
                title={row.active !== false ? "Active" : "Inactive"}
                className="shrink-0"
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Remove ${row.key || `field ${index + 1}`}`}
              className="h-10 w-10 shrink-0 text-muted-foreground"
              onClick={() => setRows(rows.length > 1 ? rows.filter((current) => current.id !== row.id) : [emptyRow(isFaq ? true : undefined)])}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {dateKey(row.key) ? (
            <DateField
              label={`${row.key} value`}
              value={row.value}
              onChange={(next) => patchRow(row.id, { value: next })}
            />
          ) : (
            <Textarea
              aria-label={`${row.key || `Field ${index + 1}`} value`}
              value={row.value}
              placeholder="Value"
              className="min-h-[88px] resize-y"
              onChange={(event) => patchRow(row.id, { value: event.target.value })}
            />
          )}
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="compact-action-button neutral-action-button gap-1.5"
        onClick={() => setRows([...rows, emptyRow(isFaq ? true : undefined)])}
      >
        <Plus className="h-3.5 w-3.5" />
        Add field
      </Button>
    </div>
  );

  return (
    <>
      <SheetHeader className="sr-only">
        <SheetTitle>{item.name || "New business context"}</SheetTitle>
        <SheetDescription>{item.associatedContact ? "Managed in the customer profile." : readOnly ? "Read-only in this demo." : "Edit the entry and check what Dibs reads."}</SheetDescription>
      </SheetHeader>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-3">
        <div className="flex min-w-0 items-center gap-3">
          {!readOnly && (
            <div className="flex items-center gap-2">
              <Switch id="gk-active" checked={active} onCheckedChange={setActive} disabled={saving} />
              <Label htmlFor="gk-active" className="text-sm">Active</Label>
            </div>
          )}

        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={saving}
            className="standard-action-button neutral-action-button"
            onClick={onClose}
          >
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button type="button" size="sm" disabled={saving} className="standard-action-button" onClick={() => void submit()}>
              {saving ? "Saving…" : "Save"}
            </Button>
          )}
          {!isNew && !readOnly && !isFaq && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label="More actions" disabled={busy || saving} className="h-8 w-8 text-muted-foreground">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="gap-2 text-destructive focus:text-destructive" onSelect={() => onDelete(original)}>
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {error && <p role="alert" className="shrink-0 border-b border-border px-6 py-2 text-sm text-destructive">{error}</p>}

        <div className="h-[30%] shrink-0 overflow-y-auto border-b border-border bg-card p-6">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-foreground">What Dibs reads</h3>
            <InfoTip label="About what Dibs reads">
              {editableText || isFaq || isOffer ? "This updates as you type. It's the text Dibs sees when it uses this knowledge." : "The text Dibs got from the original source."}
            </InfoTip>
            {!isNew && item.canProcess && (
              <div className="ml-auto flex items-center gap-2">
                <StatusDot status={item.status} />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={item.type === "website" ? "Refresh from website" : "Read again"}
                  title={item.type === "website" ? "Refresh from website" : item.status === "Ready" ? "Read again" : "Try again"}
                  disabled={refreshing || item.status === "Processing"}
                  className="h-8 w-8 text-muted-foreground"
                  onClick={async () => {
                    setRefreshing(true);
                    try { await onRefresh(item); } finally { setRefreshing(false); }
                  }}
                >
                  {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </div>
          <pre className="mt-3 whitespace-pre-wrap break-words rounded-lg border border-border bg-muted/40 p-4 font-mono text-xs leading-5 text-foreground">
            {preview}
          </pre>
        </div>

        <fieldset disabled={saving} className="min-h-0 flex-1 basis-0 space-y-6 overflow-y-auto p-6">
          <ContactAssociation item={item} />
          <div className="space-y-1.5">
            <Label htmlFor="gk-title">Title</Label>
            <Input
              id="gk-title"
              value={name}
              maxLength={120}
              readOnly={readOnly}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          {!readOnly && (
            <div className="space-y-1.5">
              <Label id="gk-category-label">Category</Label>
              {addingCategory ? (
                <div className="flex gap-2">
                  <Input
                    autoFocus
                    aria-label="New category name"
                    value={newCategory}
                    maxLength={MAX_CATEGORY}
                    placeholder="e.g. Pricing"
                    onChange={(event) => setNewCategory(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addCategory();
                      }
                      if (event.key === "Escape") {
                        event.stopPropagation();
                        setAddingCategory(false);
                        setNewCategory("");
                      }
                    }}
                  />
                  <Button type="button" size="sm" disabled={!newCategory.trim()} className="standard-action-button shrink-0" onClick={addCategory}>
                    Add
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="standard-action-button neutral-action-button shrink-0"
                    onClick={() => { setAddingCategory(false); setNewCategory(""); }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Select
                  value={category || NO_CATEGORY}
                  onValueChange={(value) => {
                    if (value === ADD_CATEGORY) return setAddingCategory(true);
                    setCategory(value === NO_CATEGORY ? "" : value);
                  }}
                >
                  <SelectTrigger aria-labelledby="gk-category-label"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CATEGORY}>No category</SelectItem>
                    {categories.map((entry) => <SelectItem key={entry} value={entry}>{entry}</SelectItem>)}
                    <SelectSeparator />
                    <SelectItem value={ADD_CATEGORY} className="text-primary">
                      <span className="inline-flex items-center gap-2"><Plus className="h-3.5 w-3.5" />Add new category</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {item.type === "website" && (
            <div className="space-y-1.5">
              <Label htmlFor="gk-url">Website address</Label>
              <Input id="gk-url" type="url" value={url} placeholder="https://yourbusiness.com" onChange={(event) => setUrl(event.target.value)} />
              <p className="text-xs text-muted-foreground">
                Dibs reads the text on this page. Links to other pages aren&apos;t imported.
              </p>
            </div>
          )}

          {item.type === "file" && !isNew && item.hasOriginalFile !== false && !item.isMarkdownFile && (
            <button
              type="button"
              onClick={() => onOpenFile(item)}
              className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3 text-left outline-none transition-colors hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring"
            >
              <FileText className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{item.name}</span>
                <span className="block text-xs text-muted-foreground">Open the original file</span>
              </span>
            </button>
          )}

          {(editableText || isFaq || isOffer) ? fieldsEditor : !isNew && item.type !== "website" ? (
            <p className="text-sm text-muted-foreground">
              {readOnly
                ? "This was added from a customer's profile, so it's read-only here."
                : "Dibs reads the text from this document. To change what it says, upload a new version."}
            </p>
          ) : null}
        </fieldset>

    </>
  );
}

interface GeneralKnowledgeProps {
  /** Customer knowledge is added from a customer's profile, so it's read-only here. */
  variant?: "general" | "customer";
  businessId: string | null;
  items: KnowledgeItem[];
  loading: boolean;
  loadFailed: boolean;
  onRetry: () => void;
  saving: boolean;
  busy: boolean;
  uploading: boolean;
  /** Ask the table to open this item, for example from a test answer's source. */
  requestedId: string | null;
  onRequestHandled: () => void;
  onSave: (item: KnowledgeItem) => Promise<KnowledgeItem | null>;
  onSetEnabled: (item: KnowledgeItem, enabled: boolean) => Promise<KnowledgeItem>;
  onDelete: (item: KnowledgeItem) => Promise<boolean>;
  onOpenFile: (item: KnowledgeItem) => void;
  onRefresh: (item: KnowledgeItem) => Promise<void>;
  onUpload: (files: File[]) => void;
  onTest?: () => void;
  /** Saves categories chosen before they were stored with the entries. */
  onSetCategories?: (categories: Record<string, string>) => Promise<boolean>;
}

export default function GeneralKnowledge({
  variant = "general",
  businessId, items, loading, loadFailed, onRetry, saving, busy, uploading,
  requestedId, onRequestHandled, onSave, onSetEnabled, onDelete, onOpenFile, onRefresh, onUpload, onTest,
  onSetCategories,
}: GeneralKnowledgeProps) {
  const [store, updateStore] = useStoredSettings<CategoryStore>(
    `dibs:knowledge-categories:${businessId ?? "guest"}`,
    { categories: DEFAULT_CATEGORIES, byItem: {} },
  );
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [draftItem, setDraftItem] = useState<KnowledgeItem | null>(null);
  const [deleting, setDeleting] = useState<KnowledgeItem | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  useEffect(() => {
    if (!requestedId) return;
    setDraftItem(null);
    setOpenId(requestedId);
    onRequestHandled();
  }, [requestedId, onRequestHandled]);

  const isCustomer = variant === "customer";
  // The saved category wins; one chosen in this browser before categories
  // were saved shows until it moves to the server below.
  const categoryOf = (item: KnowledgeItem) =>
    item.associatedContact ? "Customer"
      : item.associatedWorkflow ? "Workflow"
        : item.category !== undefined ? item.category.trim()
          : item.id in store.byItem ? store.byItem[item.id].trim()
            : item.type === "faq" ? "FAQ"
              : item.type === "offer" ? "Offer"
                : "";

  const categories = [...new Set([
    ...store.categories,
    ...Object.values(store.byItem),
    ...items.map((item) => item.category ?? ""),
  ].map((entry) => entry.trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));

  // One-time move of categories saved in this browser onto the entries.
  const movingCategories = useRef(false);
  useEffect(() => {
    if (isCustomer || loading || loadFailed || !businessId || !onSetCategories || movingCategories.current) return;
    const byId = new Map(items.map((item) => [item.id, item]));
    const legacy = Object.keys(store.byItem);
    if (legacy.length === 0 || items.length === 0) return;
    const pending = Object.fromEntries(legacy
      .filter((id) => {
        const item = byId.get(id);
        return item && item.category === undefined && !item.associatedContact && !item.associatedWorkflow;
      })
      .map((id) => [id, store.byItem[id].trim().slice(0, MAX_CATEGORY)]));
    movingCategories.current = true;
    const settle = (moved: boolean) => {
      // Entries that are gone, or already saved server-side, need nothing more.
      const byItem = Object.fromEntries(Object.entries(store.byItem)
        .filter(([id]) => !moved && id in pending));
      updateStore({ byItem });
    };
    if (Object.keys(pending).length === 0) return settle(true);
    void onSetCategories(pending).then(settle);
  }, [isCustomer, loading, loadFailed, businessId, onSetCategories, items, store.byItem, updateStore]);

  const visible = filterKnowledgeItems(items, query, "all", "all")
    .filter((item) => categoryFilter === "all" || categoryOf(item) === categoryFilter)
    .sort((a, b) => (Date.parse(b.updatedAt ?? "") || 0) - (Date.parse(a.updatedAt ?? "") || 0));

  const active = draftItem ?? items.find((item) => item.id === openId) ?? null;

  const closePanel = () => {
    setOpenId(null);
    setDraftItem(null);
  };

  const openItem = (item: KnowledgeItem) => {
    if (item.status === "Processing" && !item.associatedContact && item.type !== "file") return;
    setDraftItem(null);
    setOpenId(item.id);
  };

  const startNew = (type: "note" | "website" | "offer" | "faq") => {
    const created = createKnowledgeItem(type);
    setOpenId(null);
    setDraftItem({
      ...created,
      name: type === "faq" ? "Customer FAQs" : "",
      ...(type === "offer" ? { enabled: true } : {}),
    });
  };

  const addCategory = (raw: string) => {
    const name = raw.trim().slice(0, MAX_CATEGORY);
    if (!name) return "";
    const existing = categories.find((entry) => entry.toLocaleLowerCase() === name.toLocaleLowerCase());
    if (existing) return existing;
    updateStore({ categories: [...store.categories, name] });
    toast.success(`Category “${name}” added`);
    return name;
  };

  const save = async (item: KnowledgeItem, category: string, active?: boolean) => {
    const saved = await onSave({ ...item, category: category.trim() });
    if (!saved) return false;
    // Offers can be switched on and off; that's a separate backend call.
    if (active !== undefined && active !== isActive(saved)) {
      try {
        await onSetEnabled(saved, active);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "The offer status could not be updated.");
      }
    }
    if (item.id in store.byItem) {
      const byItem = { ...store.byItem };
      delete byItem[item.id];
      updateStore({ byItem });
    }
    return true;
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const removed = await onDelete(deleting);
    if (removed) {
      const byItem = { ...store.byItem };
      delete byItem[deleting.id];
      updateStore({ byItem });
      setDeleting(null);
      closePanel();
    }
  };

  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col gap-3 pt-3"
      onDragEnter={(event) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        dragDepth.current++;
        setDragging(true);
      }}
      onDragOver={(event) => { if (event.dataTransfer.types.includes("Files")) event.preventDefault(); }}
      onDragLeave={(event) => {
        event.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (!dragDepth.current) setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        if (!isCustomer) onUpload(Array.from(event.dataTransfer.files));
      }}
    >
      {dragging && !isCustomer && (
        <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-primary bg-background/95 text-sm">
          <Upload className="h-8 w-8 text-primary" />
          <p className="font-medium">Drop files to upload them</p>
          <p className="text-muted-foreground">PDF, DOCX, TXT, Markdown · Up to 10 MB each</p>
        </div>
      )}

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <div className="relative w-full max-w-[300px]">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            aria-label="Search business context"
            placeholder="Search business context..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="admin-toolbar-input pl-8"
          />
        </div>
        {!isCustomer && (
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger aria-label="Filter by category" className="admin-toolbar-button h-8 w-[170px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {[...new Set([...categories, ...items.map(categoryOf).filter(Boolean)])].sort((a, b) => a.localeCompare(b)).map((entry) => (
                <SelectItem key={entry} value={entry}>{entry}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="ml-auto flex items-center gap-2">
          {onTest && (
            <Button variant="outline" size="sm" className="admin-toolbar-button" onClick={onTest}>
              <Sparkles className="h-3.5 w-3.5" />
              Test AI
            </Button>
          )}
          {!isCustomer && <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" disabled={uploading || !businessId || isDemoMode()} className="compact-action-button gap-1.5">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                {uploading ? "Uploading…" : "Add context"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem className="items-start gap-2.5" onSelect={() => startNew("note")}>
                <Pencil className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span><span className="block text-sm">Write an entry</span><span className="block text-xs text-muted-foreground">Add fields like Price or Rule.</span></span>
              </DropdownMenuItem>
              <DropdownMenuItem className="items-start gap-2.5" onSelect={() => startNew("offer")}>
                <Tag className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span><span className="block text-sm">Add an offer</span><span className="block text-xs text-muted-foreground">Something Dibs may mention.</span></span>
              </DropdownMenuItem>
              {!items.some((entry) => entry.type === "faq") && (
                <DropdownMenuItem className="items-start gap-2.5" onSelect={() => startNew("faq")}>
                  <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span><span className="block text-sm">Add FAQs</span><span className="block text-xs text-muted-foreground">Common questions and answers.</span></span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="items-start gap-2.5" onSelect={() => fileInput.current?.click()}>
                <Upload className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span><span className="block text-sm">Upload files</span><span className="block text-xs text-muted-foreground">PDF, DOCX, TXT, Markdown</span></span>
              </DropdownMenuItem>
              <DropdownMenuItem className="items-start gap-2.5" onSelect={() => startNew("website")}>
                <Globe className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span><span className="block text-sm">Import a website</span><span className="block text-xs text-muted-foreground">Reads the text on one page.</span></span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>}
        </div>
      </div>
      <input
        ref={fileInput}
        type="file"
        multiple
        accept=".pdf,.docx,.txt,.md"
        className="hidden"
        aria-label="Upload knowledge files"
        onChange={(event) => { onUpload(Array.from(event.target.files ?? [])); event.target.value = ""; }}
      />

      {loadFailed && (
        <div role="alert" className="flex shrink-0 items-center justify-between gap-3 rounded-lg border border-destructive/30 p-4 text-sm">
          <p>Knowledge couldn&apos;t be loaded. Please try again.</p>
          <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto pb-6">
        <div className="admin-data-table-shell">
          <div className="admin-data-table-scroll w-full overflow-hidden bg-card">
            <Table className="admin-data-table" style={{ minWidth: 720 }}>
              <TableHeader className="admin-data-table-header">
                <TableRow className="admin-data-table-header-row">
                  <TableHead className="w-[300px] px-3">Title</TableHead>
                  <TableHead className="w-[160px] px-3">{isCustomer ? "Customer" : "Category"}</TableHead>
                  <TableHead className="w-[130px] px-3">Source</TableHead>
                  <TableHead className="w-[150px] px-3">Status</TableHead>
                  <TableHead className="w-[160px] px-3">Last updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="admin-data-table-body">
                {loading && businessId ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 px-3 text-center">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading knowledge" />
                    </TableCell>
                  </TableRow>
                ) : visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 px-3 text-center text-muted-foreground">
                      {items.length === 0
                        ? isCustomer ? "Customer knowledge appears here when it's added to a customer's profile." : "Add your first piece of knowledge."
                        : "No knowledge matches your search or filter."}
                    </TableCell>
                  </TableRow>
                ) : visible.map((item) => (
                  <TableRow key={item.id} className="group admin-data-table-row cursor-pointer" onClick={() => openItem(item)}>
                    <TableCell
                      role="button"
                      tabIndex={0}
                      aria-label={`Open ${item.name || "untitled knowledge"}`}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openItem(item);
                        }
                      }}
                      className="admin-data-table-cell !cursor-pointer font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-foreground/20 group-hover:bg-secondary"
                    >
                      <span className="block truncate">{item.name || "Untitled"}</span>
                    </TableCell>
                    <TableCell className="admin-data-table-cell !cursor-pointer group-hover:bg-secondary">
                      {isCustomer && item.associatedContact ? (
                        <span className="flex min-w-0 items-center gap-2">
                          <Avatar className="h-6 w-6 shrink-0" aria-hidden="true">
                            <AvatarFallback className="bg-primary font-heading text-xs font-semibold text-primary-foreground">
                              {item.associatedContact.name.trim().charAt(0).toUpperCase() || "?"}
                            </AvatarFallback>
                          </Avatar>
                          <span className="truncate">{item.associatedContact.name}</span>
                        </span>
                      ) : categoryOf(item) || <span className="text-muted-foreground">None</span>}
                    </TableCell>
                    <TableCell className="admin-data-table-cell !cursor-pointer text-muted-foreground group-hover:bg-secondary">{sourceLabel(item)}</TableCell>
                    <TableCell className="admin-data-table-cell !cursor-pointer group-hover:bg-secondary"><StatusDot status={item.status} /></TableCell>
                    <TableCell className="admin-data-table-cell !cursor-pointer text-muted-foreground group-hover:bg-secondary">
                      {item.updatedAt && !Number.isNaN(Date.parse(item.updatedAt))
                        ? formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <Sheet open={active !== null} onOpenChange={(open) => { if (!open && !saving) closePanel(); }}>
        <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-[920px] [&>button.absolute]:hidden">
          {active && (
            <KnowledgePanel
              key={active.id}
              item={active}
              isNew={draftItem !== null}
              categories={categories}
              initialCategory={categoryOf(active)}
              saving={saving}
              busy={busy}
              onAddCategory={addCategory}
              onSave={save}
              onDelete={setDeleting}
              onOpenFile={onOpenFile}
              onRefresh={onRefresh}
              onClose={closePanel}
            />
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={deleting !== null} onOpenChange={(open) => { if (!open && !busy) setDeleting(null); }}>
        <DialogContent className="admin-modal-content">
          <DialogHeader className="admin-modal-header">
            <DialogTitle className="font-heading">Delete knowledge?</DialogTitle>
            <DialogDescription>
              “{deleting?.name || "This item"}” will be permanently removed, including any uploaded file. Dibs will stop using this content.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              className="standard-action-button admin-modal-secondary-button neutral-action-button"
              onClick={() => setDeleting(null)}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" size="sm" disabled={busy} className="standard-action-button" onClick={() => void confirmDelete()}>
              {busy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
